import type { Route } from 'vue-router';

export const JWTVQueryParamMapping = {
	lcId: 'linkContextId',
	expPC: 'expPromoCode',
	play: 'titleSlug',
	isFb: 'isFallback',
	package: 'packageShortName',
} as const;

type JWTVQueryParamMapping = typeof JWTVQueryParamMapping;
export type QueryParamKey = keyof JWTVQueryParamMapping;
type QueryParamValue = JWTVQueryParamMapping[QueryParamKey];

export type JWTVQueryParams = {
	[K in QueryParamValue]?: K extends 'isFallback' ? boolean : string;
};

/**
 * Builds a JWTV title URL with the required `package=jwt` query param and optional tracking params.
 *
 * @param options.fullPath - The title's full path (e.g. `/us/movie/inception`). Returns `null` if empty.
 * @param options.fullUrl - When `true`, prepends the absolute base URL (domain or localhost). Defaults to relative path.
 */
export function getJWTVTitleUrl(options: { fullPath: string; fullUrl?: boolean } & JWTVQueryParams): string | null {
	const { fullPath, linkContextId, expPromoCode, isFallback, fullUrl } = options;

	if (!fullPath) {
		return null;
	}
	const params = new URLSearchParams({ package: 'jwt' });
	if (linkContextId) params.set('lcId', linkContextId);
	if (expPromoCode) params.set('expPC', expPromoCode);
	if (isFallback) params.set('isFb', '1');
	const queryParams = `?${params.toString()}`;
	let baseUrl = `https://www.${JW_CONFIG.DOMAIN}`;
	if (process.client && window.location.hostname === 'localhost') {
		baseUrl = `http://localhost:8080`;
	}
	if (fullUrl) {
		return `${baseUrl}${fullPath}${queryParams}`;
	}

	return `${fullPath}${queryParams}`;
}

/**
 * Parses a Vue Router query object into typed JWTV query params using `JWTVQueryParamMapping`.
 * Non-string values (arrays, null, undefined) are skipped.
 *
 * @param routerQuery - The raw query object from `route.query`.
 * @returns Typed `JWTVQueryParams` with only the recognized keys populated.
 */
export function getJWTVQueryParams(routerQuery: Route['query']): JWTVQueryParams {
	const params: JWTVQueryParams = {};

	Object.entries(JWTVQueryParamMapping).forEach(([queryKey, paramKey]) => {
		const value = routerQuery[queryKey];
		if (typeof value !== 'string') return;

		if (paramKey === 'isFallback') {
			params[paramKey] = value === '1';
		} else {
			params[paramKey] = value;
		}
	});

	return params;
}
